import { useEffect, useMemo, useRef } from 'react';
import type { ShouldRevalidateFunction } from 'react-router';
import type BoltMetaArgs from 'types/BoltMetaArgs';
import type { CapiProductPage } from 'types/capi/pdp';

import DownloadAppBlock from '~/components/DownloadAppBlock';
import PageLoadErrorBoundary from '~/components/PageLoadErrorBoundary';
import RecommendedArticles from '~/components/RecommendedArticles';
import PdpViewEvent from '~/events/PdpViewEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import useViewEvent from '~/hooks/useViewEvent';
import { getAlternateTagsForRegions } from '~/utils/alternateTags';
import seo from '~/utils/seo';

import { Carousel } from '../components/Carousel';
import PdpBreadcrumbs from '../components/PdpBreadcrumbs';
import ProductDetails from '../components/ProductDetails/ProductDetails';
import ProductDetailsTruncated from '../components/ProductDetails/ProductDetailsTruncated';
import ProductInformation from '../components/ProductDetails/ProductInformation';
import ProductMetaData from '../components/ProductMetaData';
import SocialSection from '../components/SocialSection';
import { getPdpViewProperties } from '../pdpViewProperties';
import Banner from './Banner';
import MoreColours from './MoreColours';
import ProductCardCarousels from './ProductCardCarousels/ProductCardCarousels';
import Rating from './Rating';
import RelatedCategories from './RelatedCategories';
import WhereToBuyComingSoon from './WhereToBuyComingSoon';
import WhereToBuyInStock from './WhereToBuyInStock';

export default function ProductPage() {
  const { clientIp, isBot } = useRootData();
  const user = useUser();
  const product = usePageData<CapiProductPage>();

  useViewEvent({
    viewProperties: useMemo(() => getPdpViewProperties(product), [product]),
    viewType: 'pdp'
  });

  useStructuredData(product._schemas);

  const userEventData = useRef({
    clientIp,
    isBot,
    user
  });

  useEffect(() => {
    new PdpViewEvent({ ...userEventData.current, product }).send();
  }, [product]);

  return (
    <div key={product.product_id}>
      <Banner />
      <div className="content-width flex flex-col items-center pt-4">
        <div className="hidden w-full max-w-[552px] pb-8 pt-4 md:flex lg:max-w-none">
          <PdpBreadcrumbs />
        </div>

        <div className="flex w-full max-w-[552px] flex-col gap-8 pb-12 md:gap-10 lg:max-w-none">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-6 md:gap-10 lg:grid-cols-12 lg:gap-6">
            <div className="flex flex-col gap-8 md:col-span-6 md:gap-10 lg:col-span-8 xl:col-span-8">
              <Carousel />
              <div className="flex flex-col gap-2">
                <ProductMetaData />
                {product.rating?.count && product.rating?.count > 0 && (
                  <Rating />
                )}
              </div>
              <MoreColours />
              <div className="hidden lg:block">
                <ProductDetails />
              </div>
            </div>
            {['coming-soon', 'tbc'].includes(product.stock_status) ? (
              <WhereToBuyComingSoon />
            ) : (
              <WhereToBuyInStock />
            )}
          </div>
          <div className="lg:hidden">
            <ProductDetailsTruncated />
          </div>
          <div className="block lg:hidden">
            <ProductInformation />
          </div>
          <SocialSection />
          <RelatedCategories />
        </div>
      </div>
      <div>
        <ProductCardCarousels />
        <DownloadAppBlock
          downloadLink="https://thesolesupplier.onelink.me/BPkz/m35evxoc"
          pagePosition="Body"
          qrCode="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/download_app_pdp_qr_code.png"
        />
        {product.recent_articles.length > 0 && (
          <>
            <hr className="w-full border-grey-midnight-read"></hr>
            <div className="bg-concrete">
              <RecommendedArticles
                articles={product.recent_articles}
                viewMoreNewsPath={product.view_more_news_path}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return <PageLoadErrorBoundary pageType="pdp" />;
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt/login')) return true;
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};

type LoaderData = {
  pageData: CapiProductPage;
};

export const handle = { i18n: ['pdp'] };

export { loader } from './loader.server';

export const meta = (args: BoltMetaArgs<LoaderData>) => {
  const { data, location } = args;

  const meta = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  if (location.search.includes('variant=')) return seo.addMetaNoIndex(meta);
  if (location.search.includes('raffle=')) return seo.addMetaNoIndex(meta);

  return meta.concat(
    getAlternateTagsForRegions(args, data?.pageData.published_region_codes)
  );
};
